'use client';

import React, { useEffect, useState, useMemo } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { Network, ArrowDown, Activity, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { shuffleCopy, useMountShuffledList } from './shuffle-utils';

interface PredictionMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    scenario?: string;
    startNodeId?: string;
    nodes?: any[];
    steps?: any[];
    _hasHints?: boolean;
  };
}

export default function PredictionMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: PredictionMechanicProps) {
  const actualMechanicId = mechanicId || id || '';
  const isV2 = !!(payload?.steps && payload.steps.length > 0);

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={prompt || ''}
      icon={isV2 ? <EyeOff className="w-5 h-5" /> : <Activity className="w-5 h-5" />}
      iconLabel="Prediction — Navigate decisions and predict outcomes"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        isV2
          ? <BlindPredictionInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
          : <PredictionMechanicInner payload={payload} submissionState={submissionState} setAnswer={setAnswer} />
      )}
    </BaseMechanicShell>
  );
}

// V2: Blind Prediction Cascade — all steps visible at once, submit all predictions together
function BlindPredictionInner({ payload, submissionState, setAnswer }: any) {
  const shuffledSteps = useMountShuffledList(payload?.steps || []);
  const steps = useMemo(
    () => shuffledSteps.map((step: any) => ({
      ...step,
      options: Array.isArray(step.options) ? shuffleCopy(step.options) : step.options,
    })),
    [shuffledSteps]
  );
  const scenario = payload?.scenario || '';
  const [selections, setSelections] = useState<Record<string, string>>({});

  const allSelected = steps.length > 0 && steps.every((s: any) => selections[s.id]);

  useEffect(() => {
    if (allSelected) {
      const orderedIds = steps.map((s: any) => selections[s.id]);
      setAnswer(orderedIds.join(','));
    } else {
      setAnswer(null);
    }
  }, [selections, allSelected, steps, setAnswer]);

  return (
    <div className="flex flex-col gap-6 font-sans">
      <div className="p-5 bg-primary/5 border border-primary/20 rounded-2xl font-serif text-base leading-relaxed">
        <h4 className="text-xs font-sans uppercase font-bold text-primary/70 mb-2 tracking-widest flex items-center gap-2">
          <EyeOff className="w-4 h-4" /> Blind Prediction Scenario
        </h4>
        <p className="text-foreground/90">{scenario}</p>
      </div>

      <div className="flex flex-col gap-5">
        {steps.map((step: any, index: number) => {
          const selected = selections[step.id];
          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-xl border-2 transition-all ${
                selected ? 'bg-card border-border/50' : 'bg-muted/10 border-primary/30 shadow-sm'
              }`}
            >
              <h4 className="font-medium text-foreground/90 mb-3 flex gap-2 items-center">
                <span className="text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Step {index + 1}</span>
                {step.prompt}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {step.options?.map((opt: any) => (
                  <button
                    type="button"
                    key={opt.id}
                    disabled={submissionState !== 'idle' && submissionState !== 'error'}
                    onClick={() => setSelections(prev => ({ ...prev, [step.id]: opt.id }))}
                    className={`p-3 rounded-lg text-sm text-left transition-all border-2 ${
                      selected === opt.id
                        ? 'bg-primary border-primary text-primary-foreground font-medium'
                        : 'bg-background border-border hover:border-primary/50 text-foreground/80'
                    }`}
                  >
                    {opt.content}
                  </button>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {allSelected && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center text-xs font-bold uppercase tracking-widest text-primary/70 flex items-center justify-center gap-2"
        >
          <EyeOff className="w-4 h-4" />
          All predictions set — submit your blind cascade
        </motion.div>
      )}
    </div>
  );
}

function PredictionMechanicInner({ payload, submissionState, setAnswer }: any) {
  const [selectedPath, setSelectedPath] = useState<{ nodeId: string; optionId: string }[]>([]);

  const shuffledNodes = useMountShuffledList(payload?.nodes || []);
  const nodes = useMemo(
    () => shuffledNodes.map((node: any) => ({
      ...node,
      options: Array.isArray(node.options) ? shuffleCopy(node.options) : node.options,
    })),
    [shuffledNodes]
  );
  const startNodeId = payload?.startNodeId;
  const scenario = payload?.scenario || "Review the initial conditions.";

  // cascadeNodes is a derived chain built from selectedPath — memoized so
  // the useEffect dep array doesn't see a new array reference every render.
  const cascadeNodes = useMemo(() => {
    const result: any[] = [];
    let currentNode = nodes.find((n: any) => n.id === startNodeId);
    while (currentNode) {
      result.push(currentNode);
      const selection = selectedPath.find((p: any) => p.nodeId === currentNode.id);
      if (selection) {
        const opt = currentNode.options?.find((o: any) => o.id === selection.optionId);
        currentNode = opt?.nextNodeId ? nodes.find((n: any) => n.id === opt.nextNodeId) : null;
      } else {
        currentNode = null;
      }
    }
    return result;
  }, [nodes, startNodeId, selectedPath]);

  useEffect(() => {
    if (cascadeNodes.length === 0) return;
    const lastNode = cascadeNodes[cascadeNodes.length - 1];
    const selection = selectedPath.find(p => p.nodeId === lastNode.id);
    
    if (selection) {
      const opt = lastNode.options?.find((o: any) => o.id === selection.optionId);
      if (opt && !opt.nextNodeId) {
        // End of the line - submit the exact path of option IDs
        setAnswer(selectedPath.map(p => p.optionId).join(','));
        return;
      }
    }
    setAnswer(null);
  }, [selectedPath, cascadeNodes, setAnswer]);

  const handleOptionSelect = (nodeId: string, optionId: string) => {
    if (submissionState !== 'idle' && submissionState !== 'error') return;
    
    setSelectedPath(prev => {
      const idx = prev.findIndex(p => p.nodeId === nodeId);
      if (idx >= 0) {
        // Change existing choice and prune subsequent cascade
        if (prev[idx].optionId === optionId) return prev; // no change
        return [...prev.slice(0, idx), { nodeId, optionId }];
      } else {
        return [...prev, { nodeId, optionId }];
      }
    });
  };

  return (
    <div className="flex flex-col gap-6 font-sans">
      {/* SCENARIO EVENT TRIGGER */}
      <div className="p-6 bg-primary/5 border border-primary/20 rounded-2xl shadow-sm font-serif text-lg leading-relaxed relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
        <h4 className="text-xs font-sans uppercase font-bold text-primary/70 mb-3 tracking-widest flex items-center gap-2">
          <Network className="w-4 h-4" /> Initial Event Trigger
        </h4>
        <p className="text-foreground/90">{scenario}</p>
      </div>

      {/* CASCADE */}
      <div className="flex flex-col items-center">
        <AnimatePresence>
          {cascadeNodes.map((node, index) => {
            const isLastNode = index === cascadeNodes.length - 1;
            const selection = selectedPath.find(p => p.nodeId === node.id);

            return (
              <motion.div 
                key={node.id}
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                className="w-full flex flex-col items-center"
              >
                {index > 0 && (
                  <div className="w-0.5 h-10 bg-primary/30 relative">
                    <ArrowDown className="w-5 h-5 text-primary/50 absolute -bottom-2 -left-2.5" />
                  </div>
                )}
                
                <div className={`
                  w-full p-5 mt-2 rounded-xl border-2 transition-all duration-300
                  ${selection ? 'bg-card border-border/50' : 'bg-muted/10 border-primary/40 shadow-sm'}
                `}>
                  <h4 className="font-medium text-foreground/90 mb-4 flex gap-2">
                    <span className="text-muted-foreground/50 text-sm mt-0.5">Q{index + 1}.</span> 
                    {node.question}
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {node.options?.map((opt: any) => {
                      const isSelected = selection?.optionId === opt.id;
                      return (
                        <button
                          type="button"
                          key={opt.id}
                          disabled={submissionState !== 'idle' && submissionState !== 'error'}
                          onClick={() => handleOptionSelect(node.id, opt.id)}
                          className={`
                            p-4 rounded-lg text-sm text-left transition-all duration-200 border-2
                            ${isSelected 
                              ? 'bg-primary border-primary text-primary-foreground shadow-md font-medium scale-[1.02]' 
                              : 'bg-background border-border hover:border-primary/50 text-foreground/80'
                            }
                            ${submissionState !== 'idle' && submissionState !== 'error' && !isSelected ? 'opacity-50 grayscale' : ''}
                          `}
                        >
                          {opt.content}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* TERMINAL STATE INDICATOR */}
        {cascadeNodes.length > 0 && selectedPath.find(p => p.nodeId === cascadeNodes[cascadeNodes.length - 1].id)?.optionId && (
          (() => {
            const lastNode = cascadeNodes[cascadeNodes.length - 1];
            const sel = selectedPath.find(p => p.nodeId === lastNode.id);
            const opt = lastNode.options?.find((o: any) => o.id === sel?.optionId);
            
            if (opt && !opt.nextNodeId) {
              return (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8 flex flex-col items-center gap-2 text-muted-foreground"
                >
                  <ArrowDown className="w-5 h-5 text-primary/50" />
                  <span className="text-xs font-bold uppercase tracking-widest text-primary/70">Simulation Ready</span>
                </motion.div>
              );
            }
            return null;
          })()
        )}
      </div>
    </div>
  );
}
